"use client";

/**
 * Calendar — vendored from the shadcn registry (new-york v4), hand-ported for
 * react-day-picker v10.
 * Registry source: https://ui.shadcn.com/r/styles/new-york/calendar.json (retrieved 2026-06-23).
 * react-day-picker is its own primitive (no per-package radix import), so the D-15
 * import rewrite is N/A here; it is token-reskinned via classNames + cn().
 */
import { useEffect, useRef } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import {
  DayPicker,
  getDefaultClassNames,
  type DayButtonProps,
  type DayPickerProps,
} from "react-day-picker";

import { cn } from "../lib/utils";

/**
 * Custom day button (ported from the shadcn new-york v4 calendar). RDP v10 does
 * NOT emit the shadcn-custom `data-selected-single` / `data-range-*` attributes
 * that the `day_button` selection CSS targets, so without this the picked day
 * (and range) render with no highlight. We derive them from `modifiers` and keep
 * the default DayButton's focus-on-`focused` behaviour for keyboard navigation.
 */
function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: DayButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={className}
      {...props}
    />
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  components,
  ...props
}: DayPickerProps) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      data-slot="calendar"
      showOutsideDays={showOutsideDays}
      className={cn(
        "bg-background p-3 motion-reduce:[&_*]:transition-none motion-reduce:[&_*]:animate-none",
        className,
      )}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-4 sm:flex-row",
          defaultClassNames.months,
        ),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav,
        ),
        button_previous: cn(
          "text-muted-foreground hover:text-foreground size-7 rounded-md p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          "text-muted-foreground hover:text-foreground size-7 rounded-md p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          "flex h-7 w-full items-center justify-center px-7 text-sm font-medium",
          defaultClassNames.month_caption,
        ),
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "text-muted-foreground flex-1 rounded-md text-[0.8rem] font-medium select-none",
          defaultClassNames.weekday,
        ),
        week: cn("mt-2 flex w-full", defaultClassNames.week),
        day: cn(
          "group/day relative aspect-square h-full w-full p-0 text-center select-none",
          defaultClassNames.day,
        ),
        day_button: cn(
          "hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring/50 flex aspect-square size-auto w-full min-w-8 flex-col items-center justify-center gap-1 rounded-md p-0 leading-none font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:rounded-md data-[range-end=true]:rounded-md data-[range-middle=true]:rounded-none motion-reduce:transition-none",
          defaultClassNames.day_button,
        ),
        today: cn(
          "bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none",
          defaultClassNames.today,
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside,
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled,
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ className: chevronClassName, orientation, ...chevronProps }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon
                className={cn("size-4", chevronClassName)}
                {...chevronProps}
              />
            );
          }
          return (
            <ChevronRightIcon
              className={cn("size-4", chevronClassName)}
              {...chevronProps}
            />
          );
        },
        DayButton: CalendarDayButton,
        // Merge caller overrides AFTER the built-ins (destructured out of
        // `props` above) so overriding one part — e.g. Chevron — can't wipe out
        // CalendarDayButton and silently kill the selected/range highlight.
        ...components,
      }}
      {...props}
    />
  );
}

export { Calendar };
